import { Control, Controller } from "react-hook-form";
import { FileInput } from "../../components/form";
import { DateTimePicker } from "../../components/form/DateTimePicker";
import { Label } from "../../components/form/Label";
import { ToggleSwitch } from "../../components/form/ToggleSwitch";
import { HorizontalDivider } from "../../components/HorizontalDivider";
import { NotificationPreview } from "../../components/NotificationPreview";
import { PushNotificationFormValues } from "./PushNotificationForm.Types";

interface PushNotificationFormStep3Props {
  control: Control<PushNotificationFormValues>;
  formValues: PushNotificationFormValues;
  goBack: () => void;
}

const PushNotificationFormStep3 = ({
  control,
  formValues,
  goBack,
}: PushNotificationFormStep3Props) => {
  return (
    <div id="step-2" className="space-y-6">
      <div className="grid grid-cols-2 gap-4">
        <div className="flex rounded-md border border-gray-200 bg-white shadow-md dark:border-gray-700 dark:bg-gray-800 flex-col">
          <NotificationPreview
            title={formValues.title}
            body={formValues.body}
          />
        </div>
        <div className="flex flex-col max-h-screen min-h-[30rem] overflow-y-auto justify-between">
          <div className="flex flex-col">
            <Controller
              name="sendAsap"
              control={control}
              render={({ field }) => {
                const { name, value, onChange } = field;
                return (
                  <ToggleSwitch
                    name={name}
                    onChange={onChange}
                    label="Send it ASAP"
                    selected={value}
                  />
                );
              }}
            />

            {!formValues.sendAsap && (
              <>
                <Controller
                  name="sendAt"
                  control={control}
                  rules={
                    !formValues.sendAsap
                      ? {
                          required: "Date & Time is required",
                        }
                      : {}
                  }
                  render={({ field, fieldState: { error } }) => {
                    const { name, value, onChange } = field;
                    return (
                      <DateTimePicker
                        name={name}
                        onChange={onChange}
                        label="Date & Time"
                        value={value}
                        required={!formValues.sendAsap}
                        errorText={error?.message}
                      />
                    );
                  }}
                />
              </>
            )}
            <HorizontalDivider />

            <Controller
              name="allRecipients"
              control={control}
              render={({ field }) => {
                const { name, value, onChange } = field;
                return (
                  <ToggleSwitch
                    name={name}
                    onChange={onChange}
                    label="Send it to Everyone"
                    selected={value}
                  />
                );
              }}
            />
            {!formValues.allRecipients && (
              <>
                <Controller
                  name="recipientsCSVFile"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { name, onChange } = field;
                    return (
                      <FileInput
                        name={name}
                        label="Recipient CSV file (heading titled ‘unikey’ and/or ‘uos’)"
                        onChange={(e) => {
                          if (e.target.files) {
                            onChange(e.target.files[0]);
                          }
                        }}
                        errorText={error?.message}
                      />
                    );
                  }}
                />
              </>
            )}
            {formValues.shortTermAnnouncement && (
              <>
                <HorizontalDivider />

                <div className="flex gap-2 pt-6 items-center">
                  <Label
                    htmlFor="shortTermAnnouncement"
                    label="It's an emergent/short-term announcement"
                    tooltip="This will send the notification to all users, regardless of their preferences."
                  />
                </div>

                <Controller
                  name="shortTermAnnouncementExpiryDate"
                  control={control}
                  rules={
                    formValues.shortTermAnnouncement
                      ? {
                          required: "Expiry Date & Time is required",
                        }
                      : {}
                  }
                  render={({ field }) => {
                    const { name, value, onChange } = field;
                    return (
                      <DateTimePicker
                        name={name}
                        onChange={onChange}
                        label="Expiry Date & Time"
                        value={value}
                        disabled={!formValues.shortTermAnnouncement}
                        required={formValues.shortTermAnnouncement}
                      />
                    );
                  }}
                />
              </>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <button
              type="button"
              onClick={goBack}
              className="text-brand bg-white border-2 border-brand focus:outline-none font-medium rounded-md text-md px-5 py-2.5 me-2 mb-2 w-full"
            >
              Back
            </button>

            <button
              name="next"
              type="submit"
              className="focus:outline-none text-white bg-brand font-medium rounded-md text-md px-5 py-2.5 me-2 mb-2 w-full"
            >
              Submit
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PushNotificationFormStep3;
